import { useState } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle, Video, FileText, ClipboardList, CheckCircle2,
  Info, User, Heart, Brain, Activity, Search, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';

/* ═══ MOCK PAZIENTI ═══ */
const PAZIENTI_DIMISSIONE = [
  { id: 'paz-a', nome: 'Mario', cognome: 'Rossi', eta: 72, reparto: 'Cardiologia', diagnosi: 'Infarto miocardico acuto — Angioplastica + 2 stent DES', adiAttivo: false },
  { id: 'paz-b', nome: 'Anna', cognome: 'Verdi', eta: 85, reparto: 'Geriatria', diagnosi: 'Scompenso cardiaco cronico riacutizzato — Nutrizione parenterale', adiAttivo: true },
];

/* ═══ SFGE MOCK QUESTIONNAIRE ═══ */
const SFGE_ITEMS = [
  { id: 'cog', area: 'Funzioni Cognitive', domanda: 'Orientamento spazio-temporale', options: ['Conservato', 'Parziale', 'Compromesso'] },
  { id: 'mob', area: 'Mobilita', domanda: 'Capacita di deambulazione', options: ['Autonomo', 'Con ausili', 'Allettato'] },
  { id: 'adl', area: 'Autonomia ADL', domanda: 'Attivita di vita quotidiana (Barthel)', options: ['Autonomo (>80)', 'Dipendente parziale (40-80)', 'Dipendente totale (<40)'] },
  { id: 'nut', area: 'Nutrizione', domanda: 'Stato nutrizionale e deglutizione', options: ['Normale', 'Disfagia lieve', 'NPT/PEG'] },
  { id: 'resp', area: 'Funzione Respiratoria', domanda: 'Necessita supporto ventilatorio', options: ['No', 'O2 terapia', 'Ventilazione'] },
  { id: 'pain', area: 'Dolore', domanda: 'Valutazione NRS al momento', options: ['0-3 (Lieve)', '4-6 (Moderato)', '7-10 (Severo)'] },
  { id: 'social', area: 'Rete Sociale', domanda: 'Caregiver disponibile al domicilio', options: ['24h', 'Parziale', 'Assente'] },
];

export default function OspedaleConcordata() {
  const [selectedPaz, setSelectedPaz] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [moduliAttivi, setModuliAttivi] = useState(false);
  const [sfge, setSfge] = useState({});
  const [bozzaPai, setBozzaPai] = useState('');
  const [showTeleconsulto, setShowTeleconsulto] = useState(false);

  const completedCount = Object.keys(sfge).length;
  const isComplete = completedCount === SFGE_ITEMS.length;

  const handleSelectPaz = (paz) => {
    setSelectedPaz(paz);
    setShowDropdown(false);
    setModuliAttivi(false);
    setSfge({});
    setBozzaPai('');
    setShowTeleconsulto(false);
  };

  const handleAttivaConcordata = () => {
    setModuliAttivi(true);
    toast.success('Dimissione concordata attivata', { description: `Moduli UVMO aperti per ${selectedPaz.cognome} ${selectedPaz.nome}.` });
  };

  return (
    <AppLayout role="ospedale">
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6" data-testid="ospedale-concordata">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Dimissione Concordata & UVMO</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Gestione dimissioni complesse — Valutazione socio-sanitaria e coordinamento UVMD</p>
        </div>

        {/* ═══ SELETTORE PAZIENTE ═══ */}
        <div className="relative" data-testid="paziente-selector">
          <button
            onClick={() => setShowDropdown(prev => !prev)}
            className="w-full flex items-center justify-between px-4 py-3.5 rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-600 transition-colors text-left"
            data-testid="btn-select-paziente"
          >
            <div className="flex items-center gap-3">
              <Search className="w-4 h-4 text-slate-400" />
              {selectedPaz ? (
                <span className="text-sm font-semibold text-slate-800 dark:text-white">{selectedPaz.cognome} {selectedPaz.nome} — {selectedPaz.reparto}</span>
              ) : (
                <span className="text-sm text-slate-400">Seleziona paziente in dimissione...</span>
              )}
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {showDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="absolute top-full left-0 right-0 mt-1.5 z-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden"
                data-testid="paziente-dropdown"
              >
                {PAZIENTI_DIMISSIONE.map(paz => (
                  <button
                    key={paz.id}
                    onClick={() => handleSelectPaz(paz)}
                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-100 dark:border-slate-800 last:border-b-0 text-left"
                    data-testid={`option-${paz.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center text-xs font-bold text-blue-700 dark:text-blue-300">
                        {paz.cognome[0]}{paz.nome[0]}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800 dark:text-white">{paz.cognome} {paz.nome}, {paz.eta} anni</p>
                        <p className="text-[11px] text-slate-500">{paz.reparto} — {paz.diagnosi.split('—')[0].trim()}</p>
                      </div>
                    </div>
                    <Badge className={`border-0 text-[9px] font-bold shrink-0 ${paz.adiAttivo ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'}`}>
                      {paz.adiAttivo ? 'ADI Attivo' : 'Nessun ADI'}
                    </Badge>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ═══ CARD RIASSUNTIVA PAZIENTE ═══ */}
        <AnimatePresence mode="wait">
          {selectedPaz && (
            <motion.div key={selectedPaz.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
              <Card className="border shadow-sm rounded-2xl bg-transparent border-slate-200 dark:border-slate-700" data-testid="paziente-context-card">
                <CardContent className="p-5 mb-2">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/40 flex items-center justify-center text-base font-bold text-blue-700 dark:text-blue-300 shrink-0">
                      {selectedPaz.cognome[0]}{selectedPaz.nome[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-base font-bold text-slate-800 dark:text-white">{selectedPaz.cognome} {selectedPaz.nome}</p>
                        <Badge className={`border-0 text-[10px] font-bold ${selectedPaz.adiAttivo ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'}`} data-testid="badge-adi-status">
                          {selectedPaz.adiAttivo ? 'ADI Complesso Attivo' : 'Nessun ADI Attivo'}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{selectedPaz.eta} anni &middot; Reparto: {selectedPaz.reparto}</p>
                      <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 font-medium">{selectedPaz.diagnosi}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ═══ CTA + ALERT CONDIZIONALE ═══ */}
        {selectedPaz && !moduliAttivi && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3 mt-2" data-testid="cta-section">
            {selectedPaz.adiAttivo ? (
              <>
                <Button disabled className="gap-2 rounded-xl text-sm opacity-50 cursor-not-allowed" data-testid="btn-attiva-concordata-disabled">
                  <AlertTriangle className="w-4 h-4" /> Attiva Dimissione Concordata
                </Button>
                <div className="flex items-start gap-3 px-5 py-4 rounded-2xl bg-amber-50 dark:bg-amber-950/20 border-2 border-amber-200 dark:border-amber-800/40" data-testid="alert-adi-attivo">
                  <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-amber-800 dark:text-amber-300">Paziente già in carico ADI territoriale</p>
                    <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed mt-1">
                      Impossibile attivare nuova dimissione concordata. È stata inviata segnalazione automatica alla <strong>COT-D</strong> per valutazione congiunta e aggiornamento del PAI esistente. Il Case Manager territoriale sarà notificato entro 24h.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <Button
                className="gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 h-11"
                onClick={handleAttivaConcordata}
                data-testid="btn-attiva-concordata"
              >
                <CheckCircle2 className="w-4 h-4" /> Attiva Dimissione Concordata
              </Button>
            )}
          </motion.div>
        )}

        {/* ═══ EMPTY STATE (nessun paziente) ═══ */}
        {!selectedPaz && (
          <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-state">
            <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
              <User className="w-8 h-8 text-muted-foreground/40" />
            </div>
            <p className="text-sm font-semibold text-muted-foreground">Seleziona un paziente</p>
            <p className="text-xs text-muted-foreground/70 mt-1 max-w-xs">Usa il selettore in alto per scegliere il paziente in dimissione e avviare il percorso UVMO.</p>
          </div>
        )}

        {/* ═══ MODULI UVMO (visibili solo dopo attivazione) ═══ */}
        <AnimatePresence>
          {moduliAttivi && selectedPaz && !selectedPaz.adiAttivo && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              data-testid="moduli-uvmo"
            >
              {/* Left: SFGE Questionnaire */}
              <div className="space-y-5">
                <Card className="border-0 shadow-sm rounded-2xl" data-testid="sfge-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                        <ClipboardList className="w-4 h-4 text-violet-500" />Questionario SFGE
                      </h3>
                      <Badge className={`border-0 text-[10px] font-bold ${isComplete ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>
                        {completedCount}/{SFGE_ITEMS.length} compilati
                      </Badge>
                    </div>
                    <div className="space-y-4">
                      {SFGE_ITEMS.map((item, idx) => {
                        const icons = { cog: Brain, mob: Activity, adl: User, nut: Heart, resp: Activity, pain: AlertTriangle, social: User };
                        const Icon = icons[item.id] || ClipboardList;
                        return (
                          <motion.div key={item.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }} className="space-y-1.5" data-testid={`sfge-${item.id}`}>
                            <div className="flex items-center gap-2">
                              <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                              <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{item.area}</span>
                            </div>
                            <p className="text-xs font-medium text-foreground">{item.domanda}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {item.options.map(opt => (
                                <button
                                  key={opt}
                                  onClick={() => setSfge(prev => ({ ...prev, [item.id]: opt }))}
                                  className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold border transition-all ${sfge[item.id] === opt ? 'border-violet-400 bg-violet-50 dark:bg-violet-950/20 text-violet-700 dark:text-violet-300' : 'border-border/30 text-muted-foreground hover:border-border'}`}
                                  data-testid={`sfge-${item.id}-${opt.split(' ')[0].toLowerCase()}`}
                                >
                                  {opt}
                                </button>
                              ))}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right: Bozza PAI + Teleconsulto */}
              <div className="space-y-5">
                <Card className="border-0 shadow-sm rounded-2xl" data-testid="bozza-pai-card">
                  <CardContent className="p-6 space-y-4">
                    <h3 className="text-sm font-bold text-foreground flex items-center gap-2"><FileText className="w-4 h-4 text-teal-500" />Bozza PAI di Dimissione</h3>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-50 dark:bg-teal-950/20 border border-teal-200 dark:border-teal-800/40">
                      <Info className="w-3.5 h-3.5 text-teal-500 shrink-0" />
                      <p className="text-[10px] text-teal-700 dark:text-teal-400">Definisci il piano assistenziale preliminare per {selectedPaz.cognome} {selectedPaz.nome}. Sara integrato dal Case Manager territoriale.</p>
                    </div>
                    <Textarea
                      value={bozzaPai}
                      onChange={e => setBozzaPai(e.target.value)}
                      placeholder={"Descrizione del piano assistenziale proposto:\n\n- Tipologia di assistenza necessaria\n- Frequenza accessi previsti\n- Dispositivi/presidi richiesti\n- Obiettivi clinici a 30 giorni..."}
                      className="min-h-[220px] text-xs"
                      data-testid="textarea-bozza-pai"
                    />
                    {isComplete && bozzaPai.length > 10 && (
                      <Button className="w-full gap-2 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold" onClick={() => toast.success('Bozza PAI salvata', { description: `Dati SFGE e bozza PAI per ${selectedPaz.cognome} ${selectedPaz.nome} inviati alla COT.` })} data-testid="btn-salva-bozza">
                        <CheckCircle2 className="w-4 h-4" /> Salva e Invia alla COT
                      </Button>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-2 border-blue-200 dark:border-blue-800/40 shadow-sm rounded-2xl" data-testid="teleconsulto-card">
                  <CardContent className="p-6 space-y-4">
                    <h3 className="text-sm font-bold text-foreground flex items-center gap-2"><Video className="w-4 h-4 text-blue-500" />Telemedicina UVMD</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">Avvia una sessione di teleconsulto con il team UVMD territoriale per la valutazione congiunta di {selectedPaz.cognome} {selectedPaz.nome}.</p>
                    {showTeleconsulto ? (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-xl bg-slate-900 p-6 text-center" data-testid="teleconsulto-active">
                        <div className="w-16 h-16 rounded-full bg-blue-600 mx-auto flex items-center justify-center mb-3 animate-pulse">
                          <Video className="w-7 h-7 text-white" />
                        </div>
                        <p className="text-sm font-bold text-white">Teleconsulto in corso...</p>
                        <p className="text-[10px] text-slate-400 mt-1">Connesso con UVMD — Caso: {selectedPaz.cognome} {selectedPaz.nome}</p>
                        <Button variant="destructive" className="mt-4 gap-2 rounded-xl" onClick={() => { setShowTeleconsulto(false); toast.info('Teleconsulto terminato'); }} data-testid="btn-chiudi-teleconsulto">Termina Sessione</Button>
                      </motion.div>
                    ) : (
                      <Button className="w-full gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold h-12" onClick={() => { setShowTeleconsulto(true); toast.success('Teleconsulto avviato', { description: 'Connessione con UVMD in corso...' }); }} data-testid="btn-avvia-teleconsulto">
                        <Video className="w-4 h-4" /> Avvia Teleconsulto con UVMD
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AppLayout>
  );
}
